import {useQueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useMemo, useState} from "react";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useEventContainerTemplates} from "~/contexts/workspaceEventContainerTemplatesContext";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {
    archiveDataViewerRow,
    EVENTS_MODULE_DATA_VIEWER_QUERY_KEY,
    isEventDataViewerRow,
} from "~/utilities/archiveEventContainerWithEvents";

type DeletePayload<T extends {id: Uuid}> = {
    rowsToDelete: Array<T>;
    rowIdsToDelete: Array<string>;
};

export function useListViewRowDelete<T extends {id: Uuid}>({
    controller,
    loadedData,
    refetchRows,
}: {
    controller: OttopilotDataViewerController<T>;
    loadedData: Array<T>;
    refetchRows: () => Promise<unknown>;
}) {
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const {showFlag} = useAnankeFlags();
    const queryClient = useQueryClient();
    const eventContainerTemplates = useEventContainerTemplates();
    const eventTemplates = useEventTemplates();

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeletingSelectedRows, setIsDeletingSelectedRows] = useState(false);
    const [hasPendingDeleteToast, setHasPendingDeleteToast] = useState(false);
    const [pendingDeletePayload, setPendingDeletePayload] = useState<DeletePayload<T> | null>(null);
    const [pendingDeleteTimeoutId, setPendingDeleteTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [optimisticallyDeletedRowIds, setOptimisticallyDeletedRowIds] = useState<Array<string>>([]);

    const selectedRows = useMemo(
        () => loadedData.filter((row) => controller.tableSelectedRowIds.includes(String(row.id))),
        [loadedData, controller.tableSelectedRowIds],
    );

    const visibleLoadedData = useMemo(
        () => loadedData.filter((row) => optimisticallyDeletedRowIds.includes(String(row.id)) !== true),
        [loadedData, optimisticallyDeletedRowIds],
    );

    const commitDeleteRows = useCallback(
        async (rowsToDelete: Array<T>, rowIdsToDelete: Array<string>) => {
            if (rowsToDelete.length === 0) {
                return;
            }
            try {
                const archivedJourney = rowsToDelete.some(
                    (row) => isEventDataViewerRow(row) !== true,
                );
                const archiveResults = await Promise.all(
                    rowsToDelete.map((row) => {
                        return archiveDataViewerRow({
                            accessToken: heimdallUser.accessToken,
                            intellsysBaseUrl,
                            workspaceId: workspace.id,
                            workspace: workspace,
                            workspaceCapabilities,
                            row,
                            queryKeyPrefix: controller.queryKeyPrefix,
                            eventContainerStatusColumnConfig:
                                isEventDataViewerRow(row)
                                    ? undefined
                                    : eventContainerTemplates.find(
                                          (template) =>
                                              template.id ===
                                              (row as T & {templateId?: Uuid}).templateId,
                                      )?.configuration.statusColumnConfig,
                            eventTemplates: eventTemplates,
                        });
                    }),
                );
                const failedArchiveResult = archiveResults.find((result) => result.success === false);
                if (failedArchiveResult !== undefined) {
                    setOptimisticallyDeletedRowIds((prev) => prev.filter((id) => !rowIdsToDelete.includes(id)));
                    showFlag({
                        title: "Archive failed",
                        description:
                            failedArchiveResult.error instanceof Error
                                ? failedArchiveResult.error.message
                                : "Unknown error",
                        appearance: "error",
                        isAutoDismiss: true,
                        autoDismissSeconds: 8,
                    });
                    return;
                }
                await Promise.all([refetchRows(), controller.rowCountQuery.refetch()]);
                void invalidateOttopilotDataViewerQueries(queryClient, controller.queryKeyPrefix);
                if (archivedJourney === true) {
                    void invalidateOttopilotDataViewerQueries(queryClient, [
                        EVENTS_MODULE_DATA_VIEWER_QUERY_KEY,
                        workspace.id,
                    ]);
                }
            } finally {
                setIsDeletingSelectedRows(false);
            }
        },
        [
            intellsysBaseUrl,
            controller.queryKeyPrefix,
            controller.rowCountQuery,
            eventTemplates,
            heimdallUser.accessToken,
            eventContainerTemplates,
            queryClient,
            refetchRows,
            showFlag,
            workspace.id,
        ],
    );

    const handleCommitDelete = useCallback(async () => {
        if (pendingDeletePayload === null) {
            return;
        }
        const payload = pendingDeletePayload;
        setHasPendingDeleteToast(false);
        setPendingDeletePayload(null);
        if (pendingDeleteTimeoutId !== null) {
            clearTimeout(pendingDeleteTimeoutId);
            setPendingDeleteTimeoutId(null);
        }
        await commitDeleteRows(payload.rowsToDelete, payload.rowIdsToDelete);
    }, [commitDeleteRows, pendingDeletePayload, pendingDeleteTimeoutId]);

    const handleUndoDelete = useCallback(() => {
        if (pendingDeletePayload === null) {
            return;
        }
        setHasPendingDeleteToast(false);
        setIsDeletingSelectedRows(false);
        setOptimisticallyDeletedRowIds((prev) =>
            prev.filter((id) => !pendingDeletePayload.rowIdsToDelete.includes(id)),
        );
        if (pendingDeleteTimeoutId !== null) {
            clearTimeout(pendingDeleteTimeoutId);
            setPendingDeleteTimeoutId(null);
        }
        setPendingDeletePayload(null);
    }, [pendingDeletePayload, pendingDeleteTimeoutId]);

    const handleDeleteSelectedRows = useCallback(() => {
        if (selectedRows.length === 0 || isDeletingSelectedRows) {
            return;
        }
        if (hasPendingDeleteToast) {
            showFlag({
                title: "Delete in progress",
                description: "Please close or undo current delete first.",
                appearance: "warning",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
            return;
        }
        setIsDeleteModalOpen(true);
    }, [hasPendingDeleteToast, isDeletingSelectedRows, selectedRows.length, showFlag]);

    const handleConfirmDeleteFromModal = useCallback(() => {
        const rowsToDelete = selectedRows;
        if (rowsToDelete.length === 0) {
            setIsDeleteModalOpen(false);
            return;
        }
        const rowIdsToDelete = rowsToDelete.map((row) => String(row.id));
        setIsDeleteModalOpen(false);
        setIsDeletingSelectedRows(true);
        setHasPendingDeleteToast(true);
        setPendingDeletePayload({rowsToDelete, rowIdsToDelete});
        const timeoutId = setTimeout(() => {
            void handleCommitDelete();
        }, 3000);
        setPendingDeleteTimeoutId(timeoutId);
        controller.setTableSelectedRowIds([]);
        setOptimisticallyDeletedRowIds((prev) => Array.from(new Set([...prev, ...rowIdsToDelete])));
    }, [controller, handleCommitDelete, selectedRows]);

    useEffect(() => {
        return () => {
            if (pendingDeleteTimeoutId !== null) {
                clearTimeout(pendingDeleteTimeoutId);
            }
        };
    }, [pendingDeleteTimeoutId]);

    const toggleRowSelected = useCallback(
        (rowId: string) => {
            controller.setTableSelectedRowIds((previous) =>
                previous.includes(rowId) ? previous.filter((id) => id !== rowId) : [...previous, rowId],
            );
        },
        [controller],
    );

    return {
        visibleLoadedData,
        selectedRows,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isDeletingSelectedRows,
        hasPendingDeleteToast,
        handleDeleteSelectedRows,
        handleConfirmDeleteFromModal,
        handleCommitDelete,
        handleUndoDelete,
        toggleRowSelected,
    };
}
